import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Rocket,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Building2,
  Mail,
  Phone,
  Target,
  TrendingUp,
  Calendar,
  FileText,
  Settings,
  ChevronRight,
  AlertCircle,
  DollarSign,
  BarChart3,
  MessageSquare,
  Loader2,
  CalendarDays,
  Plus,
  X
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';

const CopiloteAdminPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  
  // RDV slots for approval
  const [rdvSlots, setRdvSlots] = useState([]);
  const [newSlotDate, setNewSlotDate] = useState('');
  const [newSlotTime, setNewSlotTime] = useState('');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await api.get('/copilote/requests');
      setRequests(res.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (requestId, status) => {
    setProcessing(true);
    try {
      // For approval, include RDV slots
      const payload = {
        status,
        review_notes: reviewNotes,
        proposed_rdv_slots: status === 'approved' ? rdvSlots : [],
        account_manager_id: user.id
      };
      
      await api.put(`/copilote/requests/${requestId}/review`, payload);
      
      if (status === 'approved') {
        // Activate copilote subscription
        const request = requests.find(r => r.id === requestId);
        if (request) {
          await api.put(`/copilote/subscription/${request.client_id}`, {
            active: true,
            plan: 'Premium',
            price: request.budget || 1500
          });
        }
      }
      
      toast.success(status === 'approved' ? 'Demande approuvée ! Le client peut choisir un créneau RDV.' : 'Demande refusée');
      setShowDetailModal(false);
      setSelectedRequest(null);
      setReviewNotes('');
      setRdvSlots([]);
      fetchRequests();
    } catch (error) {
      toast.error('Erreur lors du traitement');
    } finally {
      setProcessing(false);
    }
  };

  const addRdvSlot = () => {
    if (!newSlotDate || !newSlotTime) {
      toast.error('Veuillez sélectionner une date et une heure');
      return;
    }
    const isoSlot = new Date(`${newSlotDate}T${newSlotTime}`).toISOString();
    if (!rdvSlots.includes(isoSlot)) {
      setRdvSlots([...rdvSlots, isoSlot]);
      setNewSlotDate('');
      setNewSlotTime('');
    }
  };

  const removeRdvSlot = (slot) => {
    setRdvSlots(rdvSlots.filter(s => s !== slot));
  };

  const formatSlotForDisplay = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statuses = {
      pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
      reviewing: { label: 'En cours', color: 'bg-blue-100 text-blue-700', icon: Eye },
      approved: { label: 'Approuvé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
      rdv_booked: { label: 'RDV réservé', color: 'bg-cyan-100 text-cyan-700', icon: CalendarDays },
      rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700', icon: XCircle }
    };
    const s = statuses[status] || statuses.pending;
    const Icon = s.icon;
    return (
      <Badge className={cn("flex items-center gap-1", s.color)}>
        <Icon className="w-3 h-3" />
        {s.label}
      </Badge>
    );
  };

  const filteredRequests = requests.filter(r => 
    filterStatus === 'all' || r.status === filterStatus
  );

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length
  };

  const channelLabels = {
    website: 'Site web',
    seo: 'SEO',
    google_ads: 'Google Ads',
    facebook: 'Facebook',
    instagram: 'Instagram',
    linkedin: 'LinkedIn',
    email: 'Email',
    sms: 'SMS'
  };

  const objectiveLabels = {
    traffic: 'Plus de trafic',
    clients: 'Plus de clients',
    basket: 'Panier moyen',
    notoriety: 'Notoriété',
    retention: 'Fidélisation',
    conversion: 'Conversion',
    roi: 'Mesure ROI',
    automation: 'Automation'
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#1a365d] flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                <Rocket className="w-5 h-5 text-white" />
              </div>
              Gestion Copilote
            </h1>
            <p className="text-[#64748b] mt-1">
              Gérez les demandes d'éligibilité et les missions Copilote
            </p>
          </div>
          
          <Button 
            className="bg-gradient-to-r from-orange-500 to-red-500"
            onClick={() => window.location.href = '/settings/copilote'}
          >
            <Settings className="w-4 h-4 mr-2" />
            Paramètres formulaire
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#64748b]">Total demandes</p>
                  <p className="text-2xl font-bold text-[#1a365d]">{stats.total}</p>
                </div>
                <Users className="w-8 h-8 text-[#94a3b8]" />
              </div>
            </CardContent>
          </Card>
          <Card className="card border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#64748b]">En attente</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="card border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#64748b]">Approuvées</p>
                  <p className="text-2xl font-bold text-emerald-600">{stats.approved}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="card border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#64748b]">Refusées</p>
                  <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <Card className="card mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Label>Filtrer par statut:</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="reviewing">En cours</SelectItem>
                  <SelectItem value="approved">Approuvées</SelectItem>
                  <SelectItem value="rejected">Refusées</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Requests List */}
        <Card className="card">
          <CardHeader>
            <CardTitle className="text-lg text-[#1a365d]">
              Demandes d'éligibilité ({filteredRequests.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="spinner" />
              </div>
            ) : filteredRequests.length === 0 ? (
              <div className="text-center py-12">
                <Rocket className="w-12 h-12 mx-auto mb-3 text-[#94a3b8]" />
                <p className="text-[#64748b]">Aucune demande pour le moment</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredRequests.map((request) => (
                  <div
                    key={request.id}
                    className="flex items-center justify-between p-4 bg-[#f8fafc] rounded-xl hover:bg-[#f1f5f9] transition-colors cursor-pointer"
                    onClick={() => {
                      setSelectedRequest(request);
                      setShowDetailModal(true);
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-100 to-emerald-100 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-cyan-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-[#1a365d]">
                          {request.company_name || request.client_name}
                        </h4>
                        <div className="flex items-center gap-3 text-sm text-[#64748b]">
                          <span>{request.sector || 'Secteur non précisé'}</span>
                          <span>•</span>
                          <span className="font-mono">{request.budget?.toLocaleString('fr-FR') || '?'}€/mois</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-xs text-[#94a3b8]">
                          {new Date(request.created_at).toLocaleDateString('fr-FR')}
                        </p>
                        {getStatusBadge(request.status)}
                      </div>
                      <ChevronRight className="w-5 h-5 text-[#94a3b8]" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#1a365d]">
                <Rocket className="w-5 h-5 text-orange-500" />
                Demande Copilote - {selectedRequest?.company_name || selectedRequest?.client_name}
              </DialogTitle>
            </DialogHeader>
            
            {selectedRequest && (
              <div className="space-y-6">
                {/* Status */}
                <div className="flex items-center justify-between p-3 bg-[#f8fafc] rounded-lg">
                  <span className="text-sm text-[#64748b]">Statut actuel</span>
                  {getStatusBadge(selectedRequest.status)}
                </div>

                {/* Contact Info */}
                <div>
                  <h4 className="font-medium text-[#1a365d] mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Contact
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-[#64748b]" />
                      <a href={`mailto:${selectedRequest.client_email}`} className="text-cyan-600 hover:underline">
                        {selectedRequest.client_email}
                      </a>
                    </div>
                    {selectedRequest.client_phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-[#64748b]" />
                        <a href={`tel:${selectedRequest.client_phone}`} className="text-cyan-600 hover:underline">
                          {selectedRequest.client_phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Company Info */}
                <div>
                  <h4 className="font-medium text-[#1a365d] mb-3 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Entreprise
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-[#64748b]">Secteur:</span>
                      <p className="font-medium">{selectedRequest.sector || '-'}</p>
                    </div>
                    <div>
                      <span className="text-[#64748b]">Panier moyen:</span>
                      <p className="font-medium">{selectedRequest.avg_basket || '-'}</p>
                    </div>
                    <div>
                      <span className="text-[#64748b]">Cible:</span>
                      <p className="font-medium">{selectedRequest.client_type || '-'}</p>
                    </div>
                    <div>
                      <span className="text-[#64748b]">Fichier client:</span>
                      <p className="font-medium">{selectedRequest.has_client_file ? 'Oui' : 'Non'}</p>
                    </div>
                  </div>
                </div>

                {/* Communication */}
                <div>
                  <h4 className="font-medium text-[#1a365d] mb-3 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Communication actuelle
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-[#64748b]">Canaux actifs:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(selectedRequest.current_channels || []).map((ch) => (
                          <Badge key={ch} variant="outline" className="text-xs">
                            {channelLabels[ch] || ch}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <span className="text-[#64748b]">Géré par:</span>
                        <p className="font-medium">{selectedRequest.who_manages_communication || '-'}</p>
                      </div>
                      <div>
                        <span className="text-[#64748b]">Force commerciale:</span>
                        <p className="font-medium">{selectedRequest.has_commercial_team ? 'Oui' : 'Non'}</p>
                      </div>
                      <div>
                        <span className="text-[#64748b]">Disponibilité 1h/sem:</span>
                        <p className={cn("font-medium", selectedRequest.available_time ? "text-emerald-600" : "text-amber-600")}>
                          {selectedRequest.available_time ? 'Oui' : 'Difficile'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Budget & Objectives */}
                <div>
                  <h4 className="font-medium text-[#1a365d] mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Budget & Objectifs
                  </h4>
                  <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 mb-3">
                    <div className="flex items-center justify-between">
                      <span className="text-cyan-700">Budget mensuel</span>
                      <span className="text-2xl font-bold text-cyan-700">
                        {selectedRequest.budget?.toLocaleString('fr-FR') || '?'}€
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="text-[#64748b] text-sm">Objectifs (par priorité):</span>
                    <div className="space-y-1 mt-2">
                      {(selectedRequest.objectives || [])
                        .sort((a, b) => (selectedRequest.objectives_priority?.[a] || 99) - (selectedRequest.objectives_priority?.[b] || 99))
                        .map((obj) => (
                          <div key={obj} className="flex items-center gap-2 text-sm">
                            <Badge className="bg-cyan-100 text-cyan-700 w-6 h-6 p-0 flex items-center justify-center">
                              #{selectedRequest.objectives_priority?.[obj] || '?'}
                            </Badge>
                            <span>{objectiveLabels[obj] || obj}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>

                {/* Expectations */}
                {selectedRequest.expectations && (
                  <div>
                    <h4 className="font-medium text-[#1a365d] mb-2 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Attentes du client
                    </h4>
                    <p className="text-sm text-[#64748b] bg-[#f8fafc] p-3 rounded-lg whitespace-pre-wrap">
                      {selectedRequest.expectations}
                    </p>
                  </div>
                )}

                {/* Review Section */}
                {selectedRequest.status === 'pending' && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-[#1a365d] mb-3">Votre décision</h4>
                    <div className="space-y-4">
                      <Textarea
                        value={reviewNotes}
                        onChange={(e) => setReviewNotes(e.target.value)}
                        placeholder="Message pour le client (optionnel)..."
                        className="min-h-[80px]"
                      />
                      
                      {/* RDV Slots Section */}
                      <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4">
                        <Label className="flex items-center gap-2 text-cyan-700 mb-3">
                          <CalendarDays className="w-4 h-4" />
                          Proposer des créneaux RDV (pour approbation)
                        </Label>
                        
                        <div className="flex gap-2 mb-3">
                          <Input
                            type="date"
                            value={newSlotDate}
                            onChange={(e) => setNewSlotDate(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            className="flex-1"
                          />
                          <Input
                            type="time"
                            value={newSlotTime}
                            onChange={(e) => setNewSlotTime(e.target.value)}
                            className="w-32"
                          />
                          <Button
                            type="button"
                            size="sm"
                            onClick={addRdvSlot}
                            className="bg-orange-500 hover:bg-cyan-600"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        {rdvSlots.length > 0 && (
                          <div className="space-y-2">
                            {rdvSlots.map((slot, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between bg-white rounded-lg px-3 py-2"
                              >
                                <span className="text-sm font-medium text-[#1a365d]">
                                  {formatSlotForDisplay(slot)}
                                </span>
                                <button
                                  onClick={() => removeRdvSlot(slot)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {rdvSlots.length === 0 && (
                          <p className="text-xs text-cyan-600">
                            Ajoutez au moins un créneau pour permettre au client de réserver son RDV de lancement.
                          </p>
                        )}
                      </div>
                      
                      <div className="flex gap-3">
                        <Button
                          className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                          onClick={() => handleReview(selectedRequest.id, 'approved')}
                          disabled={processing || rdvSlots.length === 0}
                        >
                          {processing ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mr-2" />
                          )}
                          Approuver & Proposer RDV
                        </Button>
                        <Button
                          variant="outline"
                          className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleReview(selectedRequest.id, 'rejected')}
                          disabled={processing}
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Refuser
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Review Info (if already reviewed) */}
                {selectedRequest.reviewed_at && (
                  <div className="border-t pt-4">
                    <p className="text-sm text-[#64748b]">
                      Traité le {new Date(selectedRequest.reviewed_at).toLocaleDateString('fr-FR')} 
                      {selectedRequest.reviewed_by && ` par ${selectedRequest.reviewed_by}`}
                    </p>
                    {selectedRequest.review_notes && (
                      <p className="text-sm mt-2 p-2 bg-[#f8fafc] rounded">
                        {selectedRequest.review_notes}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default CopiloteAdminPage;
